import type { GradeProgramColors, GradeProgramColorLabels } from '../services/sectionLayoutService';
import {
  type AnnualProjectProgram,
  normalizeHexColorForProjectCompare,
} from './annualProjectProgramsStorage';

function mergeUniqueHexes(...lists: string[][]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  lists.flat().forEach((hex) => {
    const n = normalizeHexColorForProjectCompare(hex);
    if (!n || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  });
  return out;
}

function createProjectId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `proj_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * 연간 프로젝트 프로그램 목록 → 기초시간표 `projectByTerm` / 라벨에 반영.
 * 학기별 프로젝트 색·이름은 연간 목록을 기준으로 맞춘다.
 */
export function applyAnnualProgramsToGradeProgramColors(
  colors: GradeProgramColors,
  programs: AnnualProjectProgram[]
): GradeProgramColors {
  const term1: string[] = [];
  const term2: string[] = [];
  const seen1 = new Set<string>();
  const seen2 = new Set<string>();
  const projectTerm1: Record<string, string> = {};
  const projectTerm2: Record<string, string> = {};
  const project: Record<string, string> = {};

  programs.forEach((p) => {
    const hex = normalizeHexColorForProjectCompare(p.colorHex);
    if (!hex) return;
    const name = (p.name ?? '').trim();
    if (p.termNumber === 1) {
      if (!seen1.has(hex)) {
        seen1.add(hex);
        term1.push(hex);
      }
      if (name) {
        projectTerm1[hex] = name;
        project[hex] = name;
      }
    } else if (p.termNumber === 2) {
      if (!seen2.has(hex)) {
        seen2.add(hex);
        term2.push(hex);
      }
      if (name) {
        projectTerm2[hex] = name;
        project[hex] = name;
      }
    }
  });

  const labels: GradeProgramColorLabels = {
    regular: { ...(colors.labels?.regular ?? {}) },
    dedicated: { ...(colors.labels?.dedicated ?? {}) },
    baseExclude: { ...(colors.labels?.baseExclude ?? {}) },
    project,
    projectTerm1,
    projectTerm2,
  };

  return {
    ...colors,
    project: mergeUniqueHexes(term1, term2),
    projectByTerm: { term1, term2 },
    labels,
  };
}

/**
 * 기초시간표 프로젝트 팔레트 → 연간 프로젝트 프로그램 목록에 맞춤.
 * 같은 학기·같은 색이면 id를 유지하고, 팔레트에 없는 항목은 제거한다.
 */
export function reconcileAnnualProgramsWithProjectColors(
  programs: AnnualProjectProgram[],
  colors: GradeProgramColors
): AnnualProjectProgram[] {
  const term1 = colors.projectByTerm?.term1 ?? colors.project ?? [];
  const term2 = colors.projectByTerm?.term2 ?? colors.project ?? [];
  const out: AnnualProjectProgram[] = [];

  const appendForTerm = (termNumber: 1 | 2, hexes: string[]) => {
    const labelMap =
      termNumber === 1
        ? { ...(colors.labels?.projectTerm1 ?? {}), ...(colors.labels?.project ?? {}) }
        : { ...(colors.labels?.projectTerm2 ?? {}), ...(colors.labels?.project ?? {}) };
    const prevForTerm = programs.filter((p) => p.termNumber === termNumber);
    const usedIds = new Set<string>();

    hexes.forEach((raw) => {
      const hex = normalizeHexColorForProjectCompare(raw);
      if (!hex) return;
      const existing = prevForTerm.find(
        (p) =>
          !usedIds.has(p.id) && normalizeHexColorForProjectCompare(p.colorHex) === hex
      );
      const label = (labelMap[hex] ?? '').trim();
      if (existing) {
        usedIds.add(existing.id);
        out.push({
          ...existing,
          colorHex: hex,
          name: label || existing.name || '프로젝트',
          termNumber,
        });
        return;
      }
      out.push({
        id: createProjectId(),
        name: label || '프로젝트',
        colorHex: hex,
        termNumber,
      });
    });
  };

  appendForTerm(1, term1);
  appendForTerm(2, term2);
  return out;
}

/** projectByTerm / 라벨이 연간 목록과 다른지 (동기화 필요 여부) */
export function gradeProgramColorsNeedAnnualProjectSync(
  colors: GradeProgramColors,
  programs: AnnualProjectProgram[]
): boolean {
  const synced = applyAnnualProgramsToGradeProgramColors(colors, programs);
  const a = colors.projectByTerm ?? { term1: [], term2: [] };
  const b = synced.projectByTerm;
  const sameList = (x: string[], y: string[]) => {
    if (x.length !== y.length) return false;
    const sx = x.map((h) => normalizeHexColorForProjectCompare(h)).filter(Boolean).sort();
    const sy = y.map((h) => normalizeHexColorForProjectCompare(h)).filter(Boolean).sort();
    return sx.every((v, i) => v === sy[i]);
  };
  if (!sameList(a.term1 ?? [], b.term1) || !sameList(a.term2 ?? [], b.term2)) return true;
  const labelKeys = Array.from(
    new Set([
      ...Object.keys(colors.labels?.projectTerm1 ?? {}),
      ...Object.keys(colors.labels?.projectTerm2 ?? {}),
      ...Object.keys(synced.labels?.projectTerm1 ?? {}),
      ...Object.keys(synced.labels?.projectTerm2 ?? {}),
    ])
  );
  for (const key of labelKeys) {
    const left1 = (colors.labels?.projectTerm1?.[key] ?? '').trim();
    const right1 = (synced.labels?.projectTerm1?.[key] ?? '').trim();
    const left2 = (colors.labels?.projectTerm2?.[key] ?? '').trim();
    const right2 = (synced.labels?.projectTerm2?.[key] ?? '').trim();
    if (left1 !== right1 || left2 !== right2) return true;
  }
  return false;
}
